/**
 * Sidebar (P26).
 *
 * Lifted verbatim from `sample-wireframe/client/src/pages/Home.tsx` — the
 * persistent left rail with brand block, search field, grouped nav, and
 * footer card. The wireframe's state-managed `<button>` items become
 * `<NavLink>`s now that we're routed; the lifted CSS has been extended to
 * cover both `button` and `a` selectors so flex layout applies identically.
 *
 * Brand icon (Network), brand subtitle ("Enterprise Platform"), and
 * search-field copy ("⌘K Search platform") match the wireframe exactly.
 */
import type { JSX } from 'react';
import { Network, Sparkles } from 'lucide-react';
import { NavLink } from 'react-router-dom';

import { useApprovals } from '@/api/index.js';
import { SECTION_GROUPS, SECTIONS, type SectionId } from './sections.js';
import { cn } from '@/lib/utils.js';

export function Sidebar(): JSX.Element {
  // Live counts for nav badges. Cheap polling via React Query default.
  const pendingApprovals = useApprovals({ state: 'pending' });
  const badges: Partial<Record<SectionId, number>> = {
    approvals: pendingApprovals.data?.items.length ?? 0,
  };

  return (
    <aside className="sidebar" aria-label="Primary">
      <div className="brand-block">
        <div className="brand-mark" aria-hidden>
          <Network size={20} />
        </div>
        <div>
          <strong>MCP Control</strong>
          <span>Enterprise Platform</span>
        </div>
      </div>

      <nav>
        {SECTION_GROUPS.map((group) => {
          // Only show items that aren't `hidden` — declutters the admin
          // nav for sections we lifted from the wireframe but haven't
          // built out (Sessions / Proxy Runtime / Triggers / Policy
          // Studio / Usage / Observability). Routes still resolve.
          const items = SECTIONS.filter((s) => s.group === group && !s.hidden);
          if (items.length === 0) return null;
          return (
            <div className="nav-group" key={group}>
              <h4>{group}</h4>
              {items.map((item) => {
                const Icon = item.icon;
                const badge = badges[item.id];
                return (
                  <NavLink
                    key={item.id}
                    to={item.path}
                    end={item.path === '/'}
                    className={({ isActive }) => cn(isActive && 'active')}
                  >
                    <Icon size={16} aria-hidden />
                    <span>{item.label}</span>
                    {badge !== undefined && badge > 0 && (
                      <span className="nav-badge" aria-label={`${badge} pending`}>
                        {badge}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </div>
          );
        })}
      </nav>

      <div className="sidebar-footer">
        <strong style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <Sparkles size={14} /> Stanford MCP Gateway
        </strong>
        <small>Stanford SSO · WG-based access</small>
      </div>
    </aside>
  );
}
